import { useToggle } from "usehooks-ts";

import Button from "components/Button";
import Input from "components/Input";
import Modal from "components/Modal";
import { strings } from "constants/strings";
import { useFormContext } from "react-hook-form";
import { IFactoringProfile, InformationFormData } from "models/HaulPay";
import dayjs from "dayjs";

interface IInvoiceDetailItem {
  invoiceNumber: string;
  invoiceDetail: string;
}

type PreviewInvoiceModalProps = {
  isOpen: boolean;
  toggleModalOpen: VoidFunction;
  toggleDetailModalOpen: VoidFunction;
  factoringProfile?: IFactoringProfile;
};

function PreviewInvoiceModal({
  isOpen,
  toggleModalOpen,
  toggleDetailModalOpen,
  factoringProfile
}: Readonly<PreviewInvoiceModalProps>) {
  const { getValues } = useFormContext<InformationFormData & IInvoiceDetailItem>();
  const selectedBillToCompany = getValues("billToCompany");

  return (
    <Modal
      isOpen={isOpen}
      closeModal={toggleModalOpen}
      wrapperClass="!my-auto desktop:!min-h-fit desktop:!w-1/2 text-base"
      dialogClass="!bg-foreground !px-4 !py-5 gap-5 desktop:!px-6 desktop:!py-7"
      closeClass="!hidden">
      <div className="flex items-center justify-between">
        <div className="font-medium">
          <h4 className="text-base desktop:text-lg">{factoringProfile?.company_profile.name}</h4>
          <span className="text-sm">{factoringProfile?.company_profile.address.street_one}</span>
          <br />
          <span className="text-sm">
            {factoringProfile?.company_profile.address.city}{" "}
            {factoringProfile?.company_profile.address.state}{" "}
            {factoringProfile?.company_profile.address.zip}
          </span>
        </div>

        <h3 className="text-xl font-semibold desktop:text-3xl">Invoice</h3>
      </div>

      <ul className="grid grid-cols-2 mt-3 text-sm">
        <li>
          <ul>
            <li>Bill To:</li>
            <li>{selectedBillToCompany?.dba ?? ""}</li>
          </ul>
        </li>
        <li className="*:grid *:grid-cols-2">
          <ul>
            <li>Invoice #</li>
            <li>INV-0000001</li>
          </ul>
          <ul>
            <li>Reference #</li>
            <li>{getValues("invoiceNumber")}</li>
          </ul>
          <ul>
            <li>Invoice Date</li>
            <li>{dayjs().format("MM/DD/YYYY hh:mm:ss A")}</li>
          </ul>
          <ul>
            <li>Due Date</li>
            <li>{dayjs().add(1, "month").format("MM/DD/YYYY hh:mm:ss A")}</li>
          </ul>
        </li>
      </ul>

      <table className="w-full font-medium table-auto text-text text-sm">
        <thead>
          <tr className="bg-card whitespace-nowrap *:text-left *:px-3 *:py-2">
            <th> </th>
            <th>Qty</th>
            <th>Rate</th>
            <th>Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr className="*:text-left *:px-3 *:py-2">
            <td>Linehaul (All-in)</td>
            <td>1</td>
            <td>{getValues("totalInvoiceAmount")}</td>
            <td>{getValues("totalInvoiceAmount")}</td>
          </tr>
        </tbody>
      </table>

      <div className="flex flex-col gap-2 pb-3 text-base font-medium border-b desktop:text-lg border-tertiary">
        <span>{getValues("invoiceDetail")}</span>
      </div>

      <div className="flex justify-end gap-1">
        <Button
          variant="outline"
          rounded
          className="text-sm !h-9 mobile:!p-2"
          onClick={() => {
            toggleModalOpen();
            toggleDetailModalOpen();
          }}>
          Go Back
        </Button>
        <Button rounded className="text-sm !h-9 mobile:!p-2" onClick={toggleModalOpen}>
          Add More Details
        </Button>
      </div>
    </Modal>
  );
}

type AddInvoiceDetailModalProps = {
  isOpen: boolean;
  toggleModalOpen: VoidFunction;
  factoringProfile?: IFactoringProfile;
};

export default function AddInvoiceDetailModal({
  isOpen,
  toggleModalOpen,
  factoringProfile
}: Readonly<AddInvoiceDetailModalProps>) {
  const [isPreviewInvoiceModalOpen, togglePreviewInvoiceModalOpen] = useToggle();

  const { register } = useFormContext<IInvoiceDetailItem>();

  return (
    <>
      <Modal
        isOpen={isOpen}
        closeModal={toggleModalOpen}
        wrapperClass="!my-auto desktop:!min-h-fit desktop:!w-1/2 text-sm"
        dialogClass="!bg-foreground !px-3 !pb-4 !pt-12 gap-9"
        closeClass="!top-24 !right-6 !bg-transparent !text-text">
        <div className="flex flex-col gap-3 pr-4">
          <span>{strings.CARRIER_FR.INCLUDE_DETAIL_TO_FR}</span>
          <Input
            name="invoiceNumber"
            register={register}
            label="&nbsp;&nbsp;Your Invoice Number"
            inputClass="!border-subtitle !rounded-lg !pl-5 !py-4 h-7 text-xs"
          />
          <Input
            name="invoiceDetail"
            register={register}
            label="&nbsp;&nbsp;Notes/Details"
            inputClass="!border-subtitle !rounded-lg !pl-5 !py-4 h-7 text-xs"
          />
        </div>
        <div className="flex justify-between gap-1">
          <Button
            variant="outline"
            rounded
            className="text-sm !h-9 mobile:!p-2"
            onClick={() => {
              toggleModalOpen();
              togglePreviewInvoiceModalOpen();
            }}>
            Preview Invoice
          </Button>
          <div className="flex gap-1">
            <Button
              variant="outline"
              rounded
              className="text-sm !h-9 mobile:!p-2"
              onClick={toggleModalOpen}>
              Cancel
            </Button>
            <Button rounded className="text-sm !h-9 mobile:!p-2" onClick={toggleModalOpen}>
              Add More Details
            </Button>
          </div>
        </div>
      </Modal>
      <PreviewInvoiceModal
        isOpen={isPreviewInvoiceModalOpen}
        toggleModalOpen={togglePreviewInvoiceModalOpen}
        toggleDetailModalOpen={toggleModalOpen}
        factoringProfile={factoringProfile}
      />
    </>
  );
}
